// ChecklistsV2 — Main container: list (Open/Historie tabs), template start modal, delete dialog.
// Fill/Detail view is delegated to <ChecklistFill /> via readOnly prop.

import React, { useCallback, useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import {
  Loader2, ClipboardList, ChevronRight, AlertTriangle, Clock,
  FileText, Trash2, Plus,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';

import { API_V2, hdrs, fmtDateShort, scoreColor, statusBadge } from '@/components/checklists/checklistUtils';
import { ChecklistFill } from '@/components/checklists/ChecklistFill';

const ChecklistsV2 = () => {
  const { selectedLocatie, selectedLocatieData } = useOutletContext();
  const { canManage } = useAuth();
  const [checklists, setChecklists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('overview');
  const [activeId, setActiveId] = useState(null);
  const [activeReadOnly, setActiveReadOnly] = useState(false);
  const [tab, setTab] = useState('te_doen');
  const [counts, setCounts] = useState({ te_doen: 0, historie: 0 });
  const [historieLimit, setHistorieLimit] = useState(50);
  const [deleting, setDeleting] = useState(null);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [startingTemplate, setStartingTemplate] = useState(null);
  const [meerBezig, setMeerBezig] = useState(false);
  const isManager = canManage?.() ?? false;

  const fetchChecklists = useCallback(async (skipGenerate = false, stil = false) => {
    if (!selectedLocatie) { setLoading(false); return; }
    // 6.27f: bij "Meer laden" blijft de lijst staan (geen paginabrede spinner)
    if (stil) setMeerBezig(true); else setLoading(true);
    try {
      const teamParam = isManager ? '&include_team=true' : '';
      const skipParam = skipGenerate ? '&skip_generate=true' : '';
      // v18.4: de server bepaalt de tab-inhoud, de sortering en de paginering —
      // zo blijven openstaande checklists bovenaan staan, ook bij 150+ ingevulde.
      const limiet = tab === 'historie' ? historieLimit : 100;
      const res = await fetch(
        `${API_V2}/checklists?locatie_id=${selectedLocatie}&tab=${tab}&limit=${limiet}${teamParam}${skipParam}`,
        { headers: hdrs() });
      if (res.ok) setChecklists(await res.json());
      const cRes = await fetch(`${API_V2}/checklists/counts?locatie_id=${selectedLocatie}`, { headers: hdrs() });
      if (cRes.ok) setCounts(await cRes.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); setMeerBezig(false); }   // F3a: knop nooit laten hangen
  }, [selectedLocatie, isManager, tab, historieLimit]);

  // E14h: bij "Meer laden" blijft de lijst staan (stil=true), alleen de knop laadt.
  const meerRef = React.useRef(false);
  useEffect(() => { fetchChecklists(false, meerRef.current); meerRef.current = false; },
           [fetchChecklists]);

  const openFill = (id) => { setActiveId(id); setActiveReadOnly(false); setView('fill'); };
  const openDetail = (id) => { setActiveId(id); setActiveReadOnly(true); setView('fill'); };
  const backToOverview = () => { setView('overview'); setActiveId(null); fetchChecklists(); };

  const handleDeleteChecklist = async () => {
    if (!deleting) return;
    try {
      const res = await fetch(`${API_V2}/checklists/${deleting.id}`, { method: 'DELETE', headers: hdrs() });
      if (res.ok) { toast.success('Checklist verwijderd'); fetchChecklists(true); }
      else toast.error('Verwijderen mislukt');
    } catch { toast.error('Verwijderen mislukt'); }
    finally { setDeleting(null); }
  };

  const handleDeleteFromDetail = (cl) => {
    setDeleting(cl);
  };

  const handleOpenTemplateModal = async () => {
    setShowTemplateModal(true);
    setTemplatesLoading(true);
    try {
      const res = await fetch(`${API_V2}/checklist-templates`, { headers: hdrs() });
      if (res.ok) {
        const all = await res.json();
        setTemplates(all.filter(t => t.actief && !t.gepauzeerd));
      }
    } catch { toast.error('Templates laden mislukt'); }
    finally { setTemplatesLoading(false); }
  };

  const handleZelfStarten = async (templateId) => {
    setStartingTemplate(templateId);
    try {
      const res = await fetch(`${API_V2}/checklists/zelf-starten`, {
        method: 'POST', headers: hdrs(),
        body: JSON.stringify({ template_id: templateId }),
      });
      // Read body exactly once as text, then parse as JSON when needed.
      // This avoids "Body is disturbed or locked" errors on large payloads
      // (templates met secties) behind ingress/proxy.
      const bodyText = await res.text();
      if (!res.ok) {
        console.error(`Zelf-starten failed: HTTP ${res.status}`, bodyText);
        let msg = `Fout ${res.status}: Aanmaken mislukt`;
        try {
          const err = JSON.parse(bodyText);
          if (err?.detail) msg = err.detail;
        } catch { /* body niet JSON */ }
        toast.error(msg);
        return;
      }
      let newChecklist;
      try {
        newChecklist = JSON.parse(bodyText);
      } catch (parseErr) {
        console.error('Zelf-starten: ongeldige JSON response', parseErr, bodyText?.slice(0, 200));
        toast.error('Aanmaken mislukt: ongeldige server response');
        return;
      }
      toast.success('Checklist aangemaakt!');
      setShowTemplateModal(false);
      // Sequentieel: eerst overzicht verversen, dan fill-view openen,
      // voorkomt concurrent fetch race-condition.
      await fetchChecklists();
      openFill(newChecklist.id);
    } catch (e) {
      console.error('Zelf-starten network error:', e);
      toast.error(`Netwerkfout: ${e.message || 'Aanmaken mislukt'}`);
    }
    finally { setStartingTemplate(null); }
  };

  if (view === 'fill' && activeId) {
    return <ChecklistFill checklistId={activeId} onBack={backToOverview} readOnly={activeReadOnly}
      onDelete={isManager ? handleDeleteFromDetail : undefined} />;
  }

  // De server levert de lijst al in de juiste volgorde per tab (2.8).
  const displayItems = checklists;

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-[#1f2548]" /></div>;

  return (
    <div className="space-y-5 max-w-3xl mx-auto px-4 sm:px-0" data-testid="checklists-v2-page">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-[#1f2548] flex items-center gap-2" data-testid="checklists-v2-title">
            <ClipboardList className="w-6 h-6" /> Checklists
          </h1>
          <p className="text-sm text-slate-500">{selectedLocatieData?.naam}</p>
        </div>
        <Button onClick={handleOpenTemplateModal} className="bg-[#1f2548] hover:bg-[#2a3060] text-white" data-testid="zelf-starten-btn">
          <Plus className="w-4 h-4 mr-1.5" /> Checklist invullen
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-100 rounded-lg p-1" data-testid="checklist-tabs">
        <button onClick={() => setTab('te_doen')}
          className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-all ${tab === 'te_doen' ? 'bg-white text-[#1f2548] shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
          data-testid="tab-te-doen">
          Te doen {counts.te_doen > 0 && <Badge className="ml-1.5 bg-blue-100 text-blue-700 text-xs">{counts.te_doen}</Badge>}
        </button>
        <button onClick={() => setTab('historie')}
          className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-all ${tab === 'historie' ? 'bg-white text-[#1f2548] shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
          data-testid="tab-historie">
          Historie {counts.historie > 0 && <Badge variant="outline" className="ml-1.5 text-xs">{counts.historie}</Badge>}
        </button>
      </div>

      {/* List */}
      <div className="space-y-2">
        {displayItems.map(cl => {
          const sb = statusBadge(cl.status, cl.te_laat);
          const score = cl.score?.percentage;
          const isOpen = cl.status === 'open' || cl.status === 'verlopen';
          return (
            <div key={cl.id} onClick={() => isOpen ? openFill(cl.id) : openDetail(cl.id)}
              className="w-full bg-white rounded-xl border border-slate-200 p-4 text-left hover:shadow-md transition-all flex items-center gap-3 cursor-pointer"
              data-testid={`checklist-item-${cl.id}`}>
              <div className={`w-2 h-12 rounded-full flex-shrink-0 ${
                cl.status === 'gemist' ? 'bg-slate-300'
                : cl.status === 'verlopen' ? 'bg-red-500'
                : cl.status === 'open' ? 'bg-blue-500'
                : score != null ? (score >= 85 ? 'bg-green-500' : score >= 70 ? 'bg-amber-500' : 'bg-red-500')
                : 'bg-slate-300'
              }`} />
              <div className="flex-1 min-w-0">
                <p className={`font-medium text-sm truncate ${cl.status === 'gemist' ? 'text-slate-400' : 'text-slate-800'}`}>
                  {cl.template_naam}{cl.periode_label ? ` — ${cl.periode_label}` : ''}
                </p>
                {cl.invulling_modus === 'persoon' ? (
                  <p className="text-xs text-slate-400 truncate">Voor: {cl.toegewezen_aan_naam || cl.ingevuld_door_naam || '-'}</p>
                ) : (
                  tab === 'historie' && <p className="text-xs text-slate-400">Locatie-breed</p>
                )}
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <Badge className={sb.cls + ' text-xs'}>{sb.label}</Badge>
                  {cl.te_laat && cl.status !== 'verlopen' && (
                    <Badge className="bg-amber-100 text-amber-700 text-xs"><AlertTriangle className="w-3 h-3 mr-0.5" /> Te laat</Badge>
                  )}
                  {cl.status === 'verlopen' && (
                    <span className="text-xs text-red-600" data-testid={`verlopen-hint-${cl.id}`}>Verlopen — nog in te vullen</span>
                  )}
                  {cl.status === 'gemist' && (
                    <span className="text-xs text-slate-400" data-testid={`gemist-reden-${cl.id}`}>
                      Gemist — {cl.gemist_reden || 'niet meer van toepassing'}
                    </span>
                  )}
                  {tab === 'historie' && cl.ingevuld_door_naam && (
                    <span className="text-xs text-slate-400">{cl.ingevuld_door_naam} — {fmtDateShort(cl.ingevuld_op)}</span>
                  )}
                </div>
              </div>
              <div className="text-right flex-shrink-0">
                {score != null ? (
                  <span className={`text-lg font-bold ${scoreColor(score)}`}>{score}%</span>
                ) : (
                  <span className="text-xs text-slate-400">{fmtDateShort(cl.periode?.deadline)}</span>
                )}
              </div>
              {isManager && (
                <div className="flex-shrink-0" onClick={e => e.stopPropagation()}>
                  <button onClick={(e) => { e.stopPropagation(); setDeleting(cl); }}
                    className="p-1.5 rounded-lg text-slate-300 hover:text-red-500 hover:bg-red-50 transition-colors"
                    data-testid={`delete-checklist-${cl.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              )}
              <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
            </div>
          );
        })}
        {tab === 'historie' && displayItems.length >= historieLimit && (
          <Button variant="outline" className="w-full" data-testid="meer-laden-btn"
            disabled={meerBezig}
            onClick={() => { meerRef.current = true; setMeerBezig(true);
              setHistorieLimit(historieLimit + 50); }}>
            {meerBezig ? 'Laden...' : 'Meer laden'}
          </Button>
        )}
        {displayItems.length === 0 && (
          <div className="bg-white rounded-xl border border-slate-200 p-10 text-center" data-testid="empty-list">
            {tab === 'te_doen'
              ? <><Clock className="w-10 h-10 text-slate-300 mx-auto mb-2" /><p className="text-slate-400">Geen checklists te doen</p></>
              : <><FileText className="w-10 h-10 text-slate-300 mx-auto mb-2" /><p className="text-slate-400">Nog geen ingevulde checklists</p></>
            }
          </div>
        )}
      </div>

      {/* Delete Checklist Dialog */}
      <Dialog open={!!deleting} onOpenChange={() => setDeleting(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Checklist verwijderen?</DialogTitle>
            <DialogDescription>
              Weet je zeker dat je deze checklist wilt verwijderen? "{deleting?.template_naam}" — {deleting?.locatie_naam}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleting(null)}>Annuleren</Button>
            <Button onClick={handleDeleteChecklist} className="bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-delete-checklist-btn">Verwijderen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Zelf Starten — Template selectie modal */}
      <Dialog open={showTemplateModal} onOpenChange={setShowTemplateModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Checklist invullen</DialogTitle>
            <DialogDescription>Kies een template om een nieuwe checklist te starten.</DialogDescription>
          </DialogHeader>
          {templatesLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-[#1f2548]" />
            </div>
          ) : templates.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-6">Geen beschikbare templates</p>
          ) : (
            <div className="space-y-2 max-h-80 overflow-y-auto" data-testid="template-list">
              {templates.map(t => {
                const hasOpen = checklists.some(c => c.template_id === t.id && (c.status === 'open' || c.status === 'verlopen'));
                return (
                  <button key={t.id}
                    onClick={() => handleZelfStarten(t.id)}
                    disabled={startingTemplate === t.id}
                    className="w-full text-left p-3 rounded-lg border border-slate-200 hover:border-[#1f2548] hover:bg-slate-50 transition-all flex items-center gap-3"
                    data-testid={`template-option-${t.id}`}>
                    <div className="w-10 h-10 rounded-lg bg-[#1f2548]/10 flex items-center justify-center flex-shrink-0">
                      <ClipboardList className="w-5 h-5 text-[#1f2548]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-800 truncate">{t.naam}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                          {t.frequentie?.type || 'wekelijks'}
                        </Badge>
                        {t.type === 'persoonlijk' && <Badge className="bg-purple-100 text-purple-700 text-[10px] px-1.5 py-0">Persoonlijk</Badge>}
                        {hasOpen && (
                          <span className="text-[10px] text-amber-600 flex items-center gap-0.5">
                            <AlertTriangle className="w-3 h-3" /> Open checklist
                          </span>
                        )}
                      </div>
                    </div>
                    {startingTemplate === t.id
                      ? <Loader2 className="w-4 h-4 animate-spin text-[#1f2548] flex-shrink-0" />
                      : <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
                    }
                  </button>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ChecklistsV2;
